import { isCanUseCallSettings } from 'util/getCallType';
import { useCallback, useMemo } from 'react';
import { selectLocalParticipant, selectRoomMetadata } from 'state/selectors';
import { CallJoinPolicy } from 'features/AccessRequests/api/accessRequestsApi';
import { getConferenceJwtToken } from 'features/AccessRequests/utils/getConferenceJwtToken';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import { persistPrejoinCallPolicyDraft } from 'features/PreJoin/utils/policy/session';
import { syncPrejoinCallPolicyIfNeeded } from 'features/PreJoin/utils/policy/sync';
import { useApplyCallSettings } from 'features/CallSettings/hooks/useApplyCallSettings';
import { callPolicyStore } from 'state/store';
import { normalizeJoinPolicy } from 'consts';

export interface CallPolicyConfig {
    joinPolicy: CallJoinPolicy;
    moderatorsApprovalOnly: boolean;
    /** true — есть активная комната; в этом случае source of truth — roomMetadata. */
    isInRoom: boolean;
    /** Применить новую политику. В in-room — на сервер; на prejoin — draft + API при наличии JWT. */
    applyChanges: (data: { joinPolicy: CallJoinPolicy; moderatorsApprovalOnly: boolean }) => void;
}

export const useCallPolicyConfig = (): CallPolicyConfig => {
    const roomMetadata = selectRoomMetadata();
    const applyInRoom = useApplyCallSettings();
    const { getTokenAndWssUrl } = useConnectToRoom();

    const isInRoom = Boolean(selectLocalParticipant());

    const applyChanges = useCallback(
        (data: { joinPolicy: CallJoinPolicy; moderatorsApprovalOnly: boolean }) => {
            if (!isCanUseCallSettings()) {
                return;
            }

            if (isInRoom) {
                applyInRoom(data);
                return;
            }

            callPolicyStore.update(data);
            persistPrejoinCallPolicyDraft();

            void getConferenceJwtToken(getTokenAndWssUrl).then((jwtToken) => {
                void syncPrejoinCallPolicyIfNeeded(jwtToken);
            });
        },
        [isInRoom, applyInRoom, getTokenAndWssUrl],
    );

    return useMemo<CallPolicyConfig>(() => {
        if (isInRoom) {
            return {
                joinPolicy: normalizeJoinPolicy(roomMetadata?.joinApprovalData?.joinApprovalPolicy),
                moderatorsApprovalOnly: Boolean(roomMetadata?.joinApprovalData?.onlyModeratorsCanApproveWaitingUsers),
                isInRoom,
                applyChanges,
            };
        }
        return {
            joinPolicy: callPolicyStore.joinPolicy,
            moderatorsApprovalOnly: callPolicyStore.moderatorsApprovalOnly,
            isInRoom,
            applyChanges,
        };
    }, [isInRoom, roomMetadata, applyChanges]);
};
